import React, { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import * as S from './tooltip.styles';
import { TooltipProps } from './tooltip.types';
import Portal from '../portal/Portal';

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  isEnabled = true,
  placement = 'right',
  contentAppearance = 'default',
  triggerFitContent = false,
  compactMaxWidth,
  isInteractive = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const isContentHoveredRef = useRef(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const scheduleHide = () => {
    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      if (!isContentHoveredRef.current) {
        setIsVisible(false);
      }
    }, 80);
  };

  useEffect(() => () => clearHideTimeout(), []);

  useEffect(() => {
    if (isVisible && isEnabled && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();

      if (!rect) {
        throw new Error('Trigger element not found');
      }

      switch (placement) {
        case 'top':
          setPosition({
            top: rect.top - (tooltipRef.current?.offsetHeight || 0) - 8,
            left: rect.left + rect.width / 2,
          });
          break;
        case 'bottom':
          setPosition({
            top: rect.bottom + 8,
            left: rect.left + rect.width / 2,
          });
          break;
        case 'center-right':
          setPosition({
            top: rect.top,
            left: rect.right + 8,
          });
          break;
        case 'left':
          setPosition({
            top: rect.top,
            left: rect.left - (tooltipRef.current?.offsetWidth || 0) - 8,
          });
          break;
        case 'right':
        default:
          setPosition({
            top: rect.top + (tooltipRef.current?.offsetHeight || 0) / 1.5,
            left: rect.right,
          });
          break;
      }
    }
  }, [isVisible, isEnabled, triggerRef, placement]);

  const keepOpen = () => {
    clearHideTimeout();
    isContentHoveredRef.current = true;
    setIsVisible(true);
  };

  const handleMouseEnter = () => {
    if (!isEnabled) return;

    clearHideTimeout();
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    if (isInteractive) {
      scheduleHide();
      return;
    }
    setIsVisible(false);
  };

  const handleContentMouseEnter = () => {
    if (!isInteractive) return;
    keepOpen();
  };

  const handleContentMouseLeave = () => {
    if (!isInteractive) return;
    isContentHoveredRef.current = false;
    scheduleHide();
  };

  const handleContentInteraction = () => {
    if (!isInteractive) return;
    keepOpen();
  };

  // Define animation properties based on placement
  const getAnimationProps = () => {
    switch (placement) {
      case 'top':
        return {
          initial: { opacity: 0, y: 10, x: '-50%' },
          animate: { opacity: 1, y: 0, x: '-50%' },
          exit: { opacity: 0, y: 10, x: '-50%' },
        };
      case 'bottom':
        return {
          initial: { opacity: 0, y: -10, x: '-50%' },
          animate: { opacity: 1, y: 0, x: '-50%' },
          exit: { opacity: 0, y: -10, x: '-50%' },
        };
      case 'left':
        return {
          initial: { opacity: 0, x: 10, y: '-50%' },
          animate: { opacity: 1, x: 0, y: '-50%' },
          exit: { opacity: 0, x: 10, y: '-50%' },
        };
      case 'right':
      default:
        return {
          initial: { opacity: 0, x: -10, y: '-50%' },
          animate: { opacity: 1, x: 0, y: '-50%' },
          exit: { opacity: 0, x: -10, y: '-50%' },
        };
    }
  };

  return (
    <S.TooltipTrigger
      ref={triggerRef}
      $fitContent={triggerFitContent}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={isInteractive ? undefined : handleMouseLeave}
      onClickCapture={isInteractive ? handleContentInteraction : undefined}
    >
      {children}
      <Portal>
        <AnimatePresence>
          {isVisible && isEnabled && (
            <S.TooltipMotionWrapper
              as={motion.div}
              {...getAnimationProps()}
              ref={tooltipRef}
              data-tooltip-layer
              style={{ top: position.top, left: position.left }}
              onMouseEnter={handleContentMouseEnter}
              onMouseLeave={handleContentMouseLeave}
              onMouseDown={isInteractive ? handleContentInteraction : undefined}
              onClick={isInteractive ? handleContentInteraction : undefined}
            >
              {contentAppearance === 'compact' ? (
                <S.TooltipContentCompact $maxWidth={compactMaxWidth}>{content}</S.TooltipContentCompact>
              ) : (
                <S.TooltipContent>{content}</S.TooltipContent>
              )}
            </S.TooltipMotionWrapper>
          )}
        </AnimatePresence>
      </Portal>
    </S.TooltipTrigger>
  );
};
